import { filter } from 'rxjs/operators';
import { MessageHandlerService } from 'app/core';
import { Observable, of } from 'rxjs';
import { OnInit, Component, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationCancel, ActivatedRoute } from '@angular/router';
import { AuthService } from '../core/auth.service';
import { Store } from '@ngxs/store';
import { UserState } from '@sportlogiq/core/user/user.state';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { shareReplayRefCount } from '@sportlogiq/shared';
import { isUndefined as _isUndefined } from 'lodash';
import { ContextualMessage, ForgotPasswordNavigationState, LoginPageSectionsEnum } from './login.types';
import { AppRoute } from '@sportlogiq/app-routing.models';
import { environment } from 'environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
    selector: 'sl-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LoginComponent implements OnInit {
    protected environment = environment;
    protected supportButtonUrl = environment.supportRequestConfig?.staticChatUrl;
    protected activatedSection: LoginPageSectionsEnum;
    protected loginPageSections = LoginPageSectionsEnum;

    protected errorMessage: string | number;
    protected videoMessage: boolean;
    protected appRoute = AppRoute;

    protected loading: boolean;
    protected contextualMessage: ContextualMessage;

    protected loginFormGroup = this._formBuilder.group({
        email: this._formBuilder.control<string>(undefined, {
            nonNullable: true,
            validators: [Validators.required, Validators.email]
        }),
        password: this._formBuilder.control<string>(undefined, {
            nonNullable: true,
            validators: [Validators.required]
        }),
    });

    protected forgotPasswordFormGroup = new FormGroup({
        email: this._formBuilder.control<string>(undefined, {
            nonNullable: true,
            validators: [Validators.required, Validators.email]
        }),
    });

    private _currentNavigationState$: Observable<ForgotPasswordNavigationState>;

    constructor(
        private _router: Router,
        private _authService: AuthService,
        private _activatedRoute: ActivatedRoute,
        private _messageHandler: MessageHandlerService,
        private _store: Store,
        private _formBuilder: FormBuilder,
        private _destroyRef: DestroyRef
    ) {
        this._currentNavigationState$ = of(this._router.currentNavigation()?.extras.state as ForgotPasswordNavigationState).pipe(
            shareReplayRefCount()
        );

        this._activatedRoute.data.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(d => {
            this.activatedSection = d.section;
        });

        if (this._store.selectSnapshot(UserState.user)) {
            this._router.navigate(['/']);
        }

        this._router.events.pipe(
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(e => {
            if (e instanceof NavigationCancel) {
                this._authService.redirectUrl = e.url;
                if (e.url.indexOf('/videos/') >= 0) {
                    this.videoMessage = true;
                }
            }
        });
    }

    public ngOnInit(): void {
        // Delete loading animation
        const loader = document.getElementsByClassName('app-loader')[0];
        if (loader) {
            loader.remove();
        }

        this._currentNavigationState$.pipe(
            filter(state => !_isUndefined(state)),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe(state => {
            this.errorMessage = state.message;
            this.contextualMessage = {
                type: 'error',
                message: state.message,
                status: state.status,
            };
            if (this.activatedSection === this.loginPageSections.ForgotPassword) {
                this.forgotPasswordFormGroup.setValue({
                    email: state.email
                }, { emitEvent: false });

                this.forgotPasswordFormGroup.updateValueAndValidity();
            }
        });
    }

    protected handleLogin(email?: string, password?: string, bypassValidation = false): void {
        if ( this.loginFormGroup.valid || bypassValidation ) {
            this.loading = true;

            this.contextualMessage = undefined;

            const loginEmailAddress = email || this.loginFormGroup.get('email').value;
            const loginPassword = password || this.loginFormGroup.get('password').value;

            this._authService.login(loginEmailAddress, loginPassword).subscribe(
                response => {
                    const r = response;
                    if (r?.changePassword) {
                        this.loading = false;
                        this.contextualMessage = {
                            type: 'success',
                            message: `Your password must be changed. Instructions has been sent to ${r.email}.`,
                        };
                        this.activatedSection = LoginPageSectionsEnum.ForceResetEmailSent;
                    }
                },
                err => {
                    const e = err ? err.error : err;
                    this.loading = false;
                    if (err?.permissionDenied) {
                        this.contextualMessage = {
                            type: 'error',
                            message: 'You do not have permission to access this web application.',
                        };
                    } else if (e?.changePassword) {
                        this.contextualMessage = {
                            type: 'success',
                            message: `Your password must be changed. Instructions has been sent to ${e.email}.`,
                        };
                        this.activatedSection = LoginPageSectionsEnum.ForceResetEmailSent;
                    } else {
                        this.contextualMessage = {
                            type: 'error',
                            message: 'Something went wrong logging you in! Please try again or contact an administrator.',
                        };
                    }
                }
            );
        }
    }

    protected handleForgotPassword() {
        if ( this.forgotPasswordFormGroup.valid ) {
            this.loading = true;

            const emailAddress = this.forgotPasswordFormGroup.get('email').value;

            this._authService.forgotPassword(emailAddress).subscribe(
                r => {
                    this.contextualMessage = {
                        type: 'success',
                        message: `An email has been sent to ${emailAddress} to reset your password.`,
                    };
                    this.loading = false;
                },
                e => {
                    this.contextualMessage = {
                        type: 'error',
                        message: 'Something went wrong logging you in! Please try again or contact an administrator.',
                    };
                    this._messageHandler.emitError('An error occured. Please try again later.');
                    this.loading = false;
                }
            );
        }
    }

    protected passwordChangedSuccessfully(data) {
        this._authService.redirectUrl = '/';
        this.handleLogin(data.user, data.password, true);
    }
}
